import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  getInvestmentDetails,
  getPendingRiskSignature,
  getInvestmentReview,
  getPendingTermSignature,
  makeInvestment,
  IInvestmentDetails,
  IInvestmentReview,
  IRiskSignature,
  ITermSignature,
  IInvestmentConclusion
} from '@/modules/transaction/services/product-investment';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  ProductInvestmentComponents,
  EProductInvestmentSteps
} from './helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductInvestment',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const currentStep = ref(EProductInvestmentSteps.INVESTMENT);
const investmentDetails = ref<IInvestmentDetails>();
const investmentReview = ref<IInvestmentReview>();
const investmentConclusion = ref<IInvestmentConclusion>();

const isLoading = ref(true);
const isFetchingReview = ref(false);
const isFetchingInvestment = ref(false);
const hasError = ref(false);
const amount = ref(0);

const riskSignature = ref<IRiskSignature>();
const termSignature = ref<ITermSignature>();

const step = computed(() => {
  return {
    component: ProductInvestmentComponents[currentStep.value]
  };
});

const showNavbar = computed(() => {
  return currentStep.value !== EProductInvestmentSteps.CONCLUSION;
});

const productId = computed(() => {
  const { id } = route.params;

  return id;
});

const productType = computed(() => {
  const { type } = route.params;

  return type;
});

function onBack() {
  if (currentStep.value === EProductInvestmentSteps.INVESTMENT)
    return onClose();

  currentStep.value = currentStep.value - 1;
}

function onClose() {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    router.push({ name: 'productsCatalog' });
  }
}

async function getDetails() {
  try {
    isLoading.value = true;
    hasError.value = false;

    await Promise.all([
      fetchInvestmentDetails(),
      fetchRiskSignature(),
      fetchTermSignature()
    ]);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchInvestmentDetails() {
  investmentDetails.value = await getInvestmentDetails(
    productId.value,
    productType.value
  );
}

async function fetchRiskSignature() {
  riskSignature.value = await getPendingRiskSignature(
    productId.value,
    productType.value
  );
}

async function fetchTermSignature() {
  termSignature.value = await getPendingTermSignature(
    productId.value,
    productType.value
  );
}

async function fetchInvestmentReview(amountValue: number) {
  amount.value = amountValue;

  try {
    isFetchingReview.value = true;

    investmentReview.value = await getInvestmentReview(
      productId.value,
      amount.value,
      productType.value
    );

    currentStep.value = EProductInvestmentSteps.REVIEW;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  } finally {
    isFetchingReview.value = false;
  }
}

async function fetchInvestment() {
  try {
    isFetchingInvestment.value = true;

    investmentConclusion.value = await makeInvestment(
      productId.value,
      amount.value
    );

    currentStep.value = EProductInvestmentSteps.CONCLUSION;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  } finally {
    isFetchingInvestment.value = false;
  }
}

onMounted(getDetails);

provide('productId', productId);
provide('investmentDetails', investmentDetails);
provide('investmentReview', investmentReview);
provide('investmentConclusion', investmentConclusion);
provide('riskSignatureData', riskSignature);
provide('isFetchingReview', isFetchingReview);
provide('termSignatureData', termSignature);
provide('isFetchingInvestment', isFetchingInvestment);

provide('fetchInvestmentReview', fetchInvestmentReview);
provide('fetchInvestment', fetchInvestment);

return { __sfc: true,route, router, currentStep, investmentDetails, investmentReview, investmentConclusion, isLoading, isFetchingReview, isFetchingInvestment, hasError, amount, riskSignature, termSignature, step, showNavbar, productId, productType, onBack, onClose, getDetails, fetchInvestmentDetails, fetchRiskSignature, fetchTermSignature, fetchInvestmentReview, fetchInvestment, NavBar, LoaderIndicator, HelperViewStatus, BackButton, FadeTransition }
}

})